import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { FiUploadCloud, FiCheck, FiEye, FiShoppingCart, FiLogOut, FiX } from 'react-icons/fi';
import { MdColorLens, MdOutlineSearch } from "react-icons/md";
import axios from 'axios';
import api from '../../../../hooks/api';
import config from '../../../../config';
import { updateBusinessLoginCache } from '../../../../hooks/auth/useBusinessLogin';
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import './StoreTheme.css';

function StoreTheme({ businessHook }) {
    const {
        businessId,
        data: businessData,
        imagePath,
        ChatId,
        collabId
    } = businessHook || {};

    const currentCollabId = collabId || null;

    const [logoPreview, setLogoPreview] = useState(null);
    const [fileLogo, setFileLogo] = useState(null);

    const [bannerPreview, setBannerPreview] = useState(null);
    const [fileBanner, setFileBanner] = useState(null);

    const [activeColorId, setActiveColorId] = useState(1);
    const [disable, setDisable] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    const [showPreview, setShowPreview] = useState(false);

    const [cropMode, setCropMode] = useState(null); // 'logo' | 'banner' | null
    const [cropImageUrl, setCropImageUrl] = useState(null);
    const cropperRef = useRef(null);

    const colors = [
        { id: 1, title: "قرمز", code: "#D32F2F", text: "#000" },
        { id: 2, title: "سبز", code: "#388E3C", text: "#000" },
        { id: 3, title: "زرد", code: "#FBC02D", text: "#000" },
        { id: 4, title: "صورتی", code: "#C2185B", text: "#000" },
        { id: 5, title: "آبی", code: "#1976D2", text: "#000" },
        { id: 6, title: "بنفش", code: "#7B1FA2", text: "#000" },
        { id: 7, title: "نارنجی", code: "#E64A19", text: "#000" },
        { id: 8, title: "طوسی", code: "#212121", text: "#000" },
    ];

    const getImageUrl = (path) => {
        if (!path) return null;
        if (path.startsWith('http')) return path;

        let baseUrl = config?.api?.imageURL || imagePath || "";
        baseUrl = baseUrl.replace(/\/$/, "");
        const cleanPath = path.replace(/^\//, "");

        return `${baseUrl}/${cleanPath}`;
    };

    useEffect(() => {
        if (businessData) {
            let savedColor = 1;
            try {
                if (businessData.theme_json) {
                    const themeObj = typeof businessData.theme_json === 'string'
                        ? JSON.parse(businessData.theme_json)
                        : businessData.theme_json;
                    savedColor = themeObj.color_id || 1;
                }
            } catch (e) {
                console.error("خطا در پارس رنگ", e);
            }
            setActiveColorId(savedColor);

            if (businessData.image_logo) {
                const fullLogoUrl = getImageUrl(businessData.image_logo);
                setLogoPreview(fullLogoUrl);
            }

            if (businessData.image_banner) {
                const fullBannerUrl = getImageUrl(businessData.image_banner);
                setBannerPreview(fullBannerUrl);
            }
        }
    }, [businessData, imagePath]);

    const handleFileChange = (e, type) => {
        const file = e.target.files[0];
        if (file) {
            const objectUrl = URL.createObjectURL(file);
            setCropImageUrl(objectUrl);
            setCropMode(type);
        }
        e.target.value = null;
    };

    const handleCropConfirm = () => {
        if (typeof cropperRef.current?.cropper !== "undefined") {
            const canvasOptions = cropMode === 'logo'
                ? { width: 256, height: 256 }
                : { width: 1600, height: 400 };

            const canvas = cropperRef.current.cropper.getCroppedCanvas(canvasOptions);

            canvas.toBlob((blob) => {
                if (blob) {
                    const fileName = cropMode === 'logo' ? "logo_cropped.png" : "banner_cropped.png";
                    const croppedFile = new File([blob], fileName, { type: "image/png" });
                    const previewUrl = URL.createObjectURL(blob);

                    if (cropMode === 'logo') {
                        setFileLogo(croppedFile);
                        setLogoPreview(previewUrl);
                    } else if (cropMode === 'banner') {
                        setFileBanner(croppedFile);
                        setBannerPreview(previewUrl);
                    }

                    setCropMode(null);
                    setCropImageUrl(null);
                }
            }, "image/png");
        }
    };

    const handleCropCancel = () => {
        setCropMode(null);
        setCropImageUrl(null);
    };

    const handleSubmit = async () => {
        if (!activeColorId) {
            toast.error("لطفا یک رنگ برای قالب انتخاب کنید");
            return;
        }

        setDisable(true);
        setUploadProgress(0);
        let finalLogoPath = null;
        let finalBannerPath = null;

        try {
            const requestedFiles = [];
            const filesToUpload = [];

            if (fileLogo) {
                const extension = fileLogo.name.split('.').pop().toLowerCase();
                requestedFiles.push({ extension: extension, type: 'logo', business_id: businessId });
                filesToUpload.push({ file: fileLogo, type: 'logo', business_id: businessId });
            }

            if (fileBanner) {
                const extension = fileBanner.name.split('.').pop().toLowerCase();
                requestedFiles.push({ extension: extension, type: 'banner', business_id: businessId });
                filesToUpload.push({ file: fileBanner, type: 'banner', business_id: businessId });
            }

            if (requestedFiles.length > 0) {
                const linkResponse = await api.post('business/upload/presigned-url', { files: requestedFiles });
                const uploadInfos = linkResponse.data;
                const fileProgressTracker = {};

                const uploadPromises = uploadInfos.map((uploadInfo, i) => {
                    const { file, type } = filesToUpload[i];
                    const uploadUrl = uploadInfo.upload_url;
                    const filePath = uploadInfo.file_path;

                    return axios.put(uploadUrl, file, {
                        headers: {
                            'Content-Type': file.type,
                            'x-amz-acl': 'public-read'
                        },
                        onUploadProgress: (progressEvent) => {
                            fileProgressTracker[i] = { loaded: progressEvent.loaded, total: progressEvent.total };
                            let totalLoaded = 0;
                            let totalSize = 0;
                            for (const key in fileProgressTracker) {
                                totalLoaded += fileProgressTracker[key].loaded;
                                totalSize += fileProgressTracker[key].total;
                            }
                            const percentCompleted = Math.round((totalLoaded * 100) / totalSize);
                            setUploadProgress(percentCompleted);
                        }
                    }).then(() => ({ type, filePath }));
                });

                const uploadResults = await Promise.all(uploadPromises);
                uploadResults.forEach(result => {
                    if (result.type === 'logo') finalLogoPath = result.filePath;
                    if (result.type === 'banner') finalBannerPath = result.filePath;
                });
            }

            if (requestedFiles.length > 0) setUploadProgress(100);

            const payload = {
                business_id: businessId,
                theme_json: JSON.stringify({ color_id: activeColorId })
            };

            if (finalLogoPath) payload.image_logo = finalLogoPath;
            if (finalBannerPath) payload.image_banner = finalBannerPath;

            const response = await api.post('business/setting/store', payload);

            if (response.data.success || response.data.status) {
                toast.success("تنظیمات با موفقیت ذخیره شد");
                setFileLogo(null);
                setFileBanner(null);

                // دیتای آپدیت‌شده جهت سینک کش رم
                const updatedPayload = {
                    ...(businessData || {}),
                    theme_json: payload.theme_json,
                    ...(finalLogoPath ? { image_logo: finalLogoPath } : {}),
                    ...(finalBannerPath ? { image_banner: finalBannerPath } : {})
                };

                // ۱. بروزرسانی کش فقط برای فروشگاه فعال (شخصی یا همکار)
                if (ChatId) {
                    updateBusinessLoginCache(ChatId, currentCollabId, updatedPayload);
                }

                // ۲. رفرش هوک والد
                if (typeof businessHook?.refetch === 'function') {
                    businessHook.refetch();
                } else if (typeof businessHook?.mutate === 'function') {
                    businessHook.mutate(updatedPayload, false);
                }

                // ۳. ارسال ایونت به کل برنامه با تفکیک collabId
                window.dispatchEvent(new CustomEvent('businessInfoUpdated', {
                    detail: {
                        collabId: currentCollabId,
                        businessId: businessId,
                        data: updatedPayload
                    }
                }));
            } else {
                toast.error(response.data.message || "خطا در ذخیره سازی نهایی");
            }
        } catch (error) {
            toast.error("خطا در آپلود یا ارتباط با سرور. لطفا اتصال اینترنت را چک کنید.");
            console.error(error);
        } finally {
            setDisable(false);
            setTimeout(() => setUploadProgress(0), 1000);
        }
    };

    const isUploading = disable && uploadProgress > 0 && uploadProgress <= 100;
    const activeColorObj = colors.find(c => c.id === activeColorId) || colors[0];
    const previewBgColor = activeColorObj.code;
    const previewTextColor = activeColorObj.text;

    return (
        <div className="store-theme">
            {cropMode && (
                <div className="crop-modal-overlay">
                    <div className="crop-modal-content">
                        <h3>{cropMode === 'logo' ? 'برش لوگو' : 'برش بنر'}</h3>

                        {cropMode === 'logo' ? (
                            <div className="cropper-container-wrapper logo-cropper-wrapper">
                                <Cropper
                                    ref={cropperRef}
                                    style={{ height: "350px", width: "100%" }}
                                    aspectRatio={1}
                                    src={cropImageUrl}
                                    viewMode={1}
                                    dragMode="move"
                                    cropBoxMovable={false}
                                    cropBoxResizable={false}
                                    zoomable={true}
                                    background={false}
                                    responsive={true}
                                    autoCropArea={1}
                                    checkOrientation={false}
                                    guides={false}
                                    center={false}
                                    highlight={false}
                                />
                            </div>
                        ) : (
                            <div className="cropper-container-wrapper banner-cropper-wrapper">
                                <Cropper
                                    ref={cropperRef}
                                    style={{ height: "250px", width: "100%" }}
                                    aspectRatio={4 / 1}
                                    src={cropImageUrl}
                                    viewMode={1}
                                    dragMode="move"
                                    cropBoxMovable={false}
                                    cropBoxResizable={false}
                                    zoomable={true}
                                    background={true}
                                    responsive={true}
                                    autoCropArea={1}
                                    checkOrientation={false}
                                    guides={true}
                                    center={true}
                                    highlight={true}
                                />
                            </div>
                        )}

                        <div className="crop-modal-actions">
                            <button className="crop-btn confirm" onClick={handleCropConfirm}>تایید و برش</button>
                            <button className="crop-btn cancel" onClick={handleCropCancel}>انصراف</button>
                        </div>
                    </div>
                </div>
            )}

            {showPreview && (
                <div className="preview-modal-overlay">
                    <div className="preview-mobile-frame">
                        <button
                            type="button"
                            className="preview-modal-close"
                            onClick={() => setShowPreview(false)}
                            aria-label="بستن پیش‌نمایش"
                            title="بستن پیش‌نمایش"
                        >
                            <FiX size={22} aria-hidden="true" />
                        </button>
                        <div className="market preview-market" style={{ backgroundColor: `${previewBgColor}1A`, color: '#000', fontFamily: 'vazir, tahoma, sans-serif', direction: 'rtl' }}>

                            <div style={{ position: 'relative', height: '80px', backgroundColor: bannerPreview ? '#e2e8f0' : previewBgColor }}>
                                {bannerPreview ? (
                                    <img src={bannerPreview} alt="بنر" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: previewTextColor, fontWeight: 'bold' }}>
                                        رنگ تم یا بنر شما
                                    </div>
                                )}

                                <div style={{ position: 'absolute', top: '90px', left: '15px', display: 'flex', gap: '15px', zIndex: 10 }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <FiShoppingCart size={24} color="#4caf50" />
                                        <span style={{ fontSize: '10px', marginTop: '2px', color: '#000' }}>خرید</span>
                                    </div>
                                    <div
                                        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer' }}
                                        onClick={() => setShowPreview(false)}
                                    >
                                        <FiLogOut size={24} color="#f44336" />
                                        <span style={{ fontSize: '10px', marginTop: '2px', color: '#000' }}>خروج</span>
                                    </div>
                                </div>

                                <div style={{ position: 'absolute', bottom: '-25px', left: '50%', transform: 'translateX(-50%)', width: '85px', height: '85px', borderRadius: '50%', backgroundColor: '#fff', overflow: 'hidden', border: `3px solid ${previewBgColor}`, zIndex: 10, boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                                    {logoPreview ? (
                                        <img src={logoPreview} alt="لوگو" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    ) : (
                                        <div className="skeleton-shimmer" style={{ width: '100%', height: '100%' }}></div>
                                    )}
                                </div>
                            </div>

                            <div style={{ backgroundColor: 'transparent', marginTop: '30px', position: 'relative', zIndex: 1, paddingTop: '10px', paddingBottom: '15px', paddingLeft: '15px', paddingRight: '15px', textAlign: 'center' }}>
                                <div style={{ marginTop: '15px', display: 'flex', background: '#fff', borderRadius: '25px', padding: '10px 15px', alignItems: 'center', border: '1px solid #e0e0e0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                                    <input type="text" placeholder="کالای مورد نظر را جستجو کنید" disabled style={{ color: '#000', border: 'none', outline: 'none', width: '100%', background: 'transparent', padding: '0', fontFamily: 'inherit', fontSize: '0.85rem', textAlign: 'right' }} />
                                    <MdOutlineSearch size="24px" color={previewBgColor} style={{ marginRight: '10px' }} />
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', overflowX: 'hidden', marginTop: '20px', paddingBottom: '5px' }}>
                                    {['دسته 1', 'دسته 2', 'دسته 3', 'دسته 4', 'دسته 5'].map((name, i) => (
                                        <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                            <div style={{ width: '55px', height: '55px', backgroundColor: '#fff', borderRadius: '16px', display: 'flex', justifyContent: 'center', alignItems: 'center', border: `1px solid ${previewBgColor}33`, boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                                                <div className="skeleton-shimmer" style={{ width: '30px', height: '30px', borderRadius: '6px' }}></div>
                                            </div>
                                            <span style={{ fontSize: '0.7rem', color: '#000', fontWeight: 'bold' }}>{name}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div style={{ padding: '15px', display: 'flex', flexDirection: 'column', gap: '25px' }}>
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                                        <span style={{ fontWeight: 'bold', fontSize: '0.95rem', color: '#000' }}>تخفیف ویژه</span>
                                        <div style={{ flex: 1, height: '1px', background: '#000' }}></div>
                                    </div>
                                    <div style={{ display: 'flex', gap: '15px', overflowX: 'hidden' }}>
                                        {[1, 2].map(n => (
                                            <div key={n} style={{ minWidth: '140px', background: '#fff', borderRadius: '16px', padding: '10px', border: `1px solid ${previewBgColor}33`, display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: '0 2px 6px rgba(0,0,0,0.04)' }}>
                                                <div className="skeleton-shimmer" style={{ width: '100px', height: '100px', borderRadius: '12px', marginBottom: '15px' }}></div>
                                                <div className="skeleton-shimmer" style={{ width: '80px', height: '12px', borderRadius: '4px' }}></div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                                        <span style={{ fontWeight: 'bold', fontSize: '0.95rem', color: '#000' }}>محصولات</span>
                                        <div style={{ flex: 1, height: '1px', background: '#000' }}></div>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                        {[1, 2].map(n => (
                                            <div key={n} style={{ display: 'flex', background: '#fff', borderRadius: '16px', padding: '15px', border: `1px solid ${previewBgColor}33`, boxShadow: '0 2px 6px rgba(0,0,0,0.04)' }}>
                                                <div className="skeleton-shimmer" style={{ width: '90px', height: '110px', borderRadius: '12px', flexShrink: 0, marginLeft: '15px' }}></div>
                                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                                    <div className="skeleton-shimmer" style={{ width: '90%', height: '14px', borderRadius: '6px', marginBottom: '10px' }}></div>
                                                    <div className="skeleton-shimmer" style={{ width: '100%', height: '10px', borderRadius: '4px', marginBottom: '5px' }}></div>
                                                    <div className="skeleton-shimmer" style={{ width: '70%', height: '10px', borderRadius: '4px', marginBottom: '15px' }}></div>

                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto' }}>
                                                        <div className="skeleton-shimmer" style={{ width: '80px', height: '16px', borderRadius: '4px' }}></div>
                                                        <div style={{ background: previewBgColor, color: previewTextColor, borderRadius: '50%', width: '40px', height: '40px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 'bold', lineHeight: '1.2' }}>
                                                            <span>۵٪</span>
                                                            <span style={{ fontSize: '0.5rem' }}>تخفیف</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="title-section">
                <MdColorLens size={20} />
                <span>رنگ قالب فروشگاه</span>
            </div>

            <div className="colors-grid">
                {colors.map((color) => (
                    <div
                        key={color.id}
                        className={`color-item ${activeColorId === color.id ? 'active' : ''}`}
                        onClick={() => setActiveColorId(color.id)}
                        style={{ backgroundColor: color.code }}
                        title={color.title}
                    >
                        {activeColorId === color.id && <FiCheck color="#fff" size={20} className="check-icon" />}
                    </div>
                ))}
            </div>
            <div className="description">
                رنگ انتخابی در هدر، دکمه‌ها و بخش‌های مهم فروشگاه شما اعمال می‌شود.
            </div>

            <div className="separator"></div>

            <div className="title-section">
                <span>لوگوی فروشگاه</span>
            </div>

            <div className="upload-box logo-upload">
                <input
                    type="file"
                    accept="image/*"
                    id="logo-upload"
                    onChange={(e) => handleFileChange(e, 'logo')}
                />
                <label htmlFor="logo-upload" className="upload-label">
                    {logoPreview ? (
                        <img src={logoPreview} alt="لوگو" className="preview-image logo" />
                    ) : (
                        <div className="placeholder">
                            <FiUploadCloud size={30} />
                            <span>انتخاب لوگو</span>
                        </div>
                    )}
                </label>
                {logoPreview && (
                    <div className="edit-overlay" onClick={() => document.getElementById('logo-upload').click()}>
                        تغییر لوگو
                    </div>
                )}
            </div>

            <div className="separator"></div>

            <div className="title-section">
                <span>بنر تبلیغاتی (هدر)</span>
            </div>

            <div className="upload-box banner-upload">
                <input
                    type="file"
                    accept="image/*"
                    id="banner-upload"
                    onChange={(e) => handleFileChange(e, 'banner')}
                />
                <label htmlFor="banner-upload" className="upload-label">
                    {bannerPreview ? (
                        <img src={bannerPreview} alt="بنر" className="preview-image banner" />
                    ) : (
                        <div className="placeholder">
                            <FiUploadCloud size={26} color="#5693F5" />
                            <div className="placeholder-texts">
                                <span>انتخاب بنر فروشگاه</span>
                                <small>(سایز پیشنهادی: 1600x400 پیکسل)</small>
                            </div>
                        </div>
                    )}
                </label>
                {bannerPreview && (
                    <div className="edit-overlay" onClick={() => document.getElementById('banner-upload').click()}>
                        تغییر بنر
                    </div>
                )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', width: '100%', marginTop: '30px' }}>
                <div
                    onClick={() => setShowPreview(true)}
                    style={{ width: '100%', height: '50px', background: '#f0f4f8', color: '#5693F5', border: '2px solid #5693F5', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer', fontWeight: 'bold' }}
                >
                    <FiEye size={20} />
                    <span>پیش‌نمایش فروشگاه</span>
                </div>

                <div
                    className={`save-btn ${disable && !isUploading ? 'disabled' : ''} ${isUploading ? 'uploading' : ''}`}
                    onClick={!disable ? handleSubmit : null}
                    style={{ marginTop: '0', width: '100%' }}
                >
                    {isUploading && (
                        <div
                            className="progress-bar-bg"
                            style={{ width: `${uploadProgress}%` }}
                        ></div>
                    )}

                    <span className="btn-text">
                        {isUploading
                            ? `در حال آپلود... %${uploadProgress}`
                            : disable
                                ? 'در حال پردازش...'
                                : 'بروزرسانی فروشگاه'}
                    </span>
                </div>
            </div>
        </div>
    );
}

export default StoreTheme;
